// Shared Poppy / Music / SFX sliders — shadcn Slider + row layout.

import React, { useEffect, useState } from 'react';
import { Slider } from '@/components/ui/slider';

const ROWS = [
  { key: 'speechVolume' as const, label: 'Poppy' },
  { key: 'musicVolume' as const, label: 'Music' },
  { key: 'sfxVolume' as const, label: 'Effects' },
];

type VolumeKey = (typeof ROWS)[number]['key'];

export interface VolumeSliderGroupProps {
  speechVolume: number;
  musicVolume: number;
  sfxVolume: number;

  onSpeechVolumeChange: (value: number) => void;
  onMusicVolumeChange: (value: number) => void;
  onSfxVolumeChange: (value: number) => void;

  /** Dimmed and non-interactive: signed out in the library, muted in room. */
  disabled?: boolean;
}

const VolumeSliderGroup: React.FC<VolumeSliderGroupProps> = ({
  speechVolume: propSpeech,
  musicVolume: propMusic,
  sfxVolume: propSfx,
  onSpeechVolumeChange,
  onMusicVolumeChange,
  onSfxVolumeChange,
  disabled = false,
}) => {
  const [speechVolume, setSpeechVolume] = useState(propSpeech);
  const [musicVolume, setMusicVolume] = useState(propMusic);
  const [sfxVolume, setSfxVolume] = useState(propSfx);

  useEffect(() => {
    setSpeechVolume(propSpeech);
  }, [propSpeech]);
  useEffect(() => {
    setMusicVolume(propMusic);
  }, [propMusic]);
  useEffect(() => {
    setSfxVolume(propSfx);
  }, [propSfx]);

  const values: Record<VolumeKey, number> = {
    speechVolume,
    musicVolume,
    sfxVolume,
  };

  return (
    <div className="flex flex-col gap-4">
      {ROWS.map((row) => {
        const value = values[row.key];

        const onSlide = (v: number) => {
          if (row.key === 'speechVolume') {
            setSpeechVolume(v);
            onSpeechVolumeChange(v);
          } else if (row.key === 'musicVolume') {
            setMusicVolume(v);
            onMusicVolumeChange(v);
          } else {
            setSfxVolume(v);
            onSfxVolumeChange(v);
          }
        };

        return (
          <div
            key={row.key}
            className="grid grid-cols-[4.5rem_minmax(0,1fr)_3rem] items-center gap-2"
          >
            <span className="shrink-0 text-sm font-normal text-foreground">
              {row.label}
            </span>
            <Slider
              className="min-w-0 flex-1 [&_[data-slot=slider-thumb]]:size-5"
              value={[value]}
              max={100}
              min={0}
              step={1}
              disabled={disabled}
              onValueChange={([v]) => onSlide(v)}
            />
            <span className="w-12 shrink-0 text-right text-sm text-muted-foreground tabular-nums">
              {value}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default VolumeSliderGroup;
